/**
 * Round 57, A2/B — a bounded, always-on ring buffer of console output, so
 * the problem reporter can attach "what actually happened" instead of
 * making the learner (or us) guess. Patches console.log/warn/error/info/
 * debug once at app bootstrap (main.tsx) and keeps calling through to the
 * original — this is a side effect on top of the console, never a
 * replacement for it.
 *
 * Bounded two ways: MAX_ENTRIES caps how many are kept regardless of size,
 * MAX_TOTAL_CHARS caps total memory regardless of count — either one alone
 * could let the other blow up (a few huge entries, or thousands of tiny
 * ones), so both apply.
 *
 * Redacted at capture time, not at send time: nothing downstream of this
 * module ever reads the buffer for anything but a report, so there's no
 * reason to hold the unredacted form in memory for the life of the tab.
 * Two rules (owner ruling, Round 57 A2) — pattern-based, not tracking which
 * variables might hold a draft or a token through the whole codebase, which
 * can't be kept complete as the app grows:
 *   - anything long enough to plausibly be a logged essay/object dump gets
 *     truncated
 *   - anything JWT-shaped gets redacted
 * The backend (src/utils/consoleLogRedaction.ts) re-applies the same rules
 * server-side as a backstop — this is a real trust boundary, not just a UI
 * nicety, since a caller could hit the API directly.
 */

export interface ConsoleLogEntry {
  level: 'log' | 'warn' | 'error' | 'info' | 'debug';
  message: string;
  timestamp: string;
}

const MAX_ENTRIES = 300;
const MAX_MESSAGE_LENGTH = 500;
const MAX_TOTAL_CHARS = 200_000;

const JWT_PATTERN = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;

const buffer: ConsoleLogEntry[] = [];
let totalChars = 0;

function serializeArg(arg: unknown): string {
  if (typeof arg === 'string') return arg;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

function redact(message: string): string {
  const jwtRedacted = message.replace(JWT_PATTERN, '[redacted-token]');
  return jwtRedacted.length > MAX_MESSAGE_LENGTH
    ? `${jwtRedacted.slice(0, MAX_MESSAGE_LENGTH)}… [${jwtRedacted.length} chars truncated]`
    : jwtRedacted;
}

function push(level: ConsoleLogEntry['level'], args: unknown[]): void {
  const message = redact(args.map(serializeArg).join(' '));
  const entry: ConsoleLogEntry = { level, message, timestamp: new Date().toISOString() };

  buffer.push(entry);
  totalChars += message.length;

  while (buffer.length > MAX_ENTRIES || totalChars > MAX_TOTAL_CHARS) {
    const dropped = buffer.shift();
    if (dropped) totalChars -= dropped.message.length;
    else break;
  }
}

let initialized = false;

/** Call once, at app bootstrap, before anything else logs. */
export function initConsoleCapture(): void {
  if (initialized) return;
  initialized = true;

  (['log', 'warn', 'error', 'info', 'debug'] as const).forEach((level) => {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      push(level, args);
      original(...args);
    };
  });
}

/** A snapshot, not a live reference — the buffer keeps mutating after this
 *  is called. */
export function getConsoleLog(): ConsoleLogEntry[] {
  return [...buffer];
}
